import React from 'react';
import {
  Alert, Box, Button, Chip, Paper, Skeleton, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, alpha, useTheme,
} from '@mui/material';
import { TrendingDown, TrendingFlat, TrendingUp } from '@mui/icons-material';
import SearchableSelect from '../common/SearchableSelect';
import { AutoFetchState } from '../../hooks/useAutoFetch';
import { AnalysisScope } from './useAnalysisScope';
import { GradeScale } from './useGradeScale';
import { mean, pct, TrendRow, TrendSeries } from './trend';

/** Session and class pickers, shared by all three pages. `allClasses` adds an "All classes" choice. */
export const ScopeFilters: React.FC<{ scope: AnalysisScope; allClasses?: boolean }> = ({ scope, allClasses }) => {
  const classValue = allClasses && scope.raw('class') === 'all' ? 'all' : scope.classId;
  return (
    <>
      <SearchableSelect
        label="Session" value={scope.sessionId} clearable={false} minWidth={190}
        onChange={(v) => scope.set('session', v)}
        options={scope.sessions.map((s) => ({
          value: s._id,
          label: s.name,
          secondary: scope.multiCenter ? s.centerId?.name : undefined,
          badge: s._id === scope.activeSessionId ? <Chip label="Active" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: 11 }} /> : undefined,
        }))}
      />
      <SearchableSelect
        label="Class" value={classValue} clearable={false} minWidth={170}
        onChange={(v) => scope.set('class', v)}
        options={[
          ...(allClasses ? [{ value: 'all', label: 'All classes' }] : []),
          ...scope.classes.map((c) => ({ value: c._id, label: c.name })),
        ]}
      />
    </>
  );
};

const EXAM_COUNTS = [
  { value: '5', label: 'Last 5 exams' },
  { value: '10', label: 'Last 10 exams' },
  { value: '20', label: 'Last 20 exams' },
  { value: '50', label: 'All exams' },
];

export const ExamCountSelect: React.FC<{ value: number; onChange: (n: number) => void }> = ({ value, onChange }) => (
  <SearchableSelect label="Range" value={String(value)} clearable={false} minWidth={170}
    onChange={(v) => onChange(Number(v))} options={EXAM_COUNTS} />
);

/** Who or what the results below are about. */
export const ResultHeading: React.FC<{ title: React.ReactNode; subtitle?: React.ReactNode }> = ({ title, subtitle }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="h6" fontWeight={700}>{title}</Typography>
    {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
  </Box>
);

export interface Stat {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  color?: string;
}

/** The headline numbers above a chart. */
export const StatStrip: React.FC<{ stats: Stat[] }> = ({ stats }) => (
  <Box sx={{ display: 'grid', gap: 1.5, mb: 2.5, gridTemplateColumns: { xs: '1fr 1fr', md: `repeat(${stats.length}, 1fr)` } }}>
    {stats.map((s) => (
      <Paper key={s.label} variant="outlined" sx={{ borderRadius: 2.5, px: 2, py: 1.75, minWidth: 0 }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {s.label}
        </Typography>
        <Typography variant="h5" fontWeight={700} noWrap sx={{ mt: 0.5, color: s.color, fontVariantNumeric: 'tabular-nums' }}>
          {s.value}
        </Typography>
        {s.hint && (
          <Typography component="div" variant="body2" color="text.secondary" noWrap sx={{ mt: 0.25 }}>{s.hint}</Typography>
        )}
      </Paper>
    ))}
  </Box>
);

/** A signed change in percentage points, coloured by direction. */
export const Delta: React.FC<{ value: number | null; suffix?: string }> = ({ value, suffix = 'pts' }) => {
  const theme = useTheme();
  if (value == null) return <>—</>;
  const flat = Math.abs(value) < 0.05;
  const color = flat ? theme.palette.text.secondary : value > 0 ? theme.palette.success.main : theme.palette.error.main;
  const Icon = flat ? TrendingFlat : value > 0 ? TrendingUp : TrendingDown;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color }}>
      <Icon sx={{ fontSize: '1em' }} />
      {flat ? 'No change' : `${value > 0 ? '+' : ''}${value.toFixed(1)} ${suffix}`}
    </Box>
  );
};

/**
 * The chart's legend, which doubles as its filter. With everything shown,
 * clicking a chip isolates it; further clicks add or remove; removing the
 * last one shows everything again.
 */
export const SeriesChips: React.FC<{
  series: TrendSeries[];
  selected: string[];
  onChange: (ids: string[]) => void;
}> = ({ series, selected, onChange }) => {
  const all = selected.length === 0;
  const toggle = (id: string) => {
    if (all) return onChange([id]);
    const next = selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id];
    onChange(next.length === series.length ? [] : next);
  };
  return (
    <ChipRow>
      <AllChip on={all} onClick={() => onChange([])} />
      {series.map((s) => (
        <DotChip key={s.id} label={s.name} color={s.color} strong={s.overall}
          dimmed={!all && !selected.includes(s.id)} picked={!all && selected.includes(s.id)} onClick={() => toggle(s.id)} />
      ))}
    </ChipRow>
  );
};

const ChipRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>{children}</Box>
);

/** The plain "All" chip that leads every chip row. */
const AllChip: React.FC<{ on: boolean; onClick: () => void }> = ({ on, onClick }) => {
  const theme = useTheme();
  return (
    <Chip label="All" size="small" onClick={onClick} variant={on ? 'filled' : 'outlined'}
      sx={{ fontWeight: 600, ...(on && { bgcolor: alpha(theme.palette.text.primary, 0.08) }) }} />
  );
};

/** A chip with a colour dot: the look of every analysis legend and quick filter. */
const DotChip: React.FC<{
  label: string;
  color: string;
  onClick: () => void;
  picked?: boolean;
  dimmed?: boolean;
  strong?: boolean;
}> = ({ label, color, onClick, picked, dimmed, strong }) => (
  <Chip
    size="small" variant="outlined" onClick={onClick} label={label}
    icon={<Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, ml: '8px !important' }} />}
    sx={{
      fontWeight: strong || picked ? 700 : 500,
      opacity: dimmed ? 0.45 : 1,
      borderColor: picked ? color : undefined,
      bgcolor: picked ? alpha(color, 0.1) : undefined,
      transition: 'background-color 120ms, border-color 120ms, opacity 120ms',
    }}
  />
);

/** Pick one option, or `allId` for all of them; looks like the trend legend chips. */
export const PickChips: React.FC<{
  options: Array<{ id: string; name: string; color: string }>;
  value: string;
  allId: string;
  onChange: (id: string) => void;
}> = ({ options, value, allId, onChange }) => (
  <ChipRow>
    <AllChip on={value === allId} onClick={() => onChange(allId)} />
    {options.map((o) => (
      <DotChip key={o.id} label={o.name} color={o.color} picked={o.id === value} onClick={() => onChange(o.id)} />
    ))}
  </ChipRow>
);

/** Every score behind a trend chart: one row per series, one column per exam, plus the average. */
export const TrendTable: React.FC<{ rows: TrendRow[]; series: TrendSeries[]; grades: GradeScale }> = ({ rows, series, grades }) => {
  const theme = useTheme();
  const head = { fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap', fontSize: 12 } as const;
  const ordered = [...series.filter((s) => !s.overall), ...series.filter((s) => s.overall)];
  const cell = (v: number | null | undefined, strong?: boolean) => (
    v == null ? <Typography variant="body2" color="text.disabled">—</Typography> : (
      <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 600, color: grades.color(v), fontVariantNumeric: 'tabular-nums' }}>
        {pct(v)}
      </Typography>
    )
  );
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ '& td, & th': { borderColor: theme.palette.divider } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={head}>Subject</TableCell>
            {rows.map((r) => <TableCell key={r.examId} align="right" sx={head}>{r.examName}</TableCell>)}
            <TableCell align="right" sx={head}>Average</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ordered.map((s) => (
            <TableRow key={s.id} sx={s.overall ? { bgcolor: alpha(theme.palette.text.primary, 0.03) } : undefined}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color }} />
                  <Typography variant="body2" fontWeight={s.overall ? 700 : 500}>{s.name}</Typography>
                </Box>
              </TableCell>
              {rows.map((r) => (
                <TableCell key={r.examId} align="right">
                  {r.absent.includes(s.name)
                    ? <Typography variant="body2" color="warning.main" fontWeight={600}>Absent</Typography>
                    : cell(r.values[s.id], s.overall)}
                </TableCell>
              ))}
              <TableCell align="right">{cell(mean(rows.map((r) => r.values[s.id])), true)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

/**
 * Error, first load, empty and loaded states for an analysis result. While a
 * newer result loads the current one stays, slightly faded.
 */
export const ResultBody: React.FC<{
  state: AutoFetchState<unknown>;
  empty: React.ReactNode | null;
  children: React.ReactNode;
}> = ({ state, empty, children }) => {
  if (state.error) {
    return (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={state.retry}>Try again</Button>}>
        {state.error}
      </Alert>
    );
  }
  if (state.loading && !state.data) {
    return (
      <Box>
        <Box sx={{ display: 'grid', gap: 1.5, mb: 2.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={96} sx={{ borderRadius: 2.5 }} />)}
        </Box>
        <Skeleton variant="rounded" height={420} sx={{ borderRadius: 2.5 }} />
      </Box>
    );
  }
  if (empty) return <>{empty}</>;
  return <Box sx={{ opacity: state.loading ? 0.6 : 1, transition: 'opacity 150ms' }}>{children}</Box>;
};
